import React, { useState, forwardRef } from "react";
import { node, bool, func, number, string, shape } from "prop-types";
import { motion } from "framer-motion";
import { twMerge } from "tailwind-merge";

import ArrowDownIcon from "@components/shared/Icons/ArrowDown";

import { withTabNavigation } from "@hoc/withTabNavigation";

const HeaderDiv = withTabNavigation("div");

const CollapsableContainer = forwardRef(function CollapsableContainer(
  props,
  ref
) {
  const {
    children,
    className,
    defaultOpen = false,
    isDisabled,
    isHeadTextHidden,
    isOpen,
    onChange,
    subTitle = null,
    title,
    titleIndex,
    ...restProps
  } = props;

  const isControlled = isOpen !== undefined;
  const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen);

  const currentIsOpen = isControlled ? isOpen : internalIsOpen;

  const onHeaderContainerClick = () => {
    if (isDisabled) {
      return;
    }

    onChange?.(currentIsOpen);

    if (!isControlled) {
      setInternalIsOpen(!currentIsOpen);
    }
  };

  return (
    <div
      className={twMerge("p-1 px-7 md:p-1", className?.wrap)}
      ref={ref}
      {...restProps}
    >
      <HeaderDiv
        className={twMerge(
          "flex justify-between items-center text-2xl/1 transition duration-300 p-2 pl-1",
          "border-b-2 border-b-primaryOrange",
          isDisabled
            ? "pointer-events-none text-primaryDarkAccent/80"
            : "cursor-pointer text-primaryDark",
          "hover:text-primaryOrange",
          isHeadTextHidden && "hidden",
          className?.headerContainer
        )}
        isOpen={currentIsOpen}
        isDisabled={isDisabled}
        onClick={onHeaderContainerClick}
      >
        <h3
          className={twMerge(
            "font-semibold tracking-wider lowercase select-none w-full py-2",
            className?.headerTitle
          )}
        >
          {titleIndex && <span className="lg:hidden">{titleIndex}. </span>}
          {title}
          {subTitle && (
            <>
              <span
                className={twMerge(
                  "font-semibold tracking-wider select-none w-full text-sm/1 -mt-0.5 block opacity-80",
                  className?.headerSubTitle
                )}
              >
                {subTitle}
              </span>
            </>
          )}
        </h3>
        {!isDisabled && (
          <ArrowDownIcon
            className={twMerge(
              "transition duration-300 text-primaryOrange",
              currentIsOpen && "rotate-180"
            )}
            height="16px"
            width="16px"
          />
        )}
      </HeaderDiv>
      <motion.div
        animate={currentIsOpen ? "open" : "closed"}
        exit="closed"
        className="overflow-y-clip"
        initial={currentIsOpen ? "open" : "closed"}
        key="accordion"
        variants={{
          open: {
            height: "auto",
            opacity: 1,
          },
          closed: {
            height: 0,
            opacity: 0,
            transition: { duration: 0.3, type: "tween" },
            transitionEnd: { display: "none" },
          },
        }}
      >
        <div className={twMerge("pt-4", className?.bodyContainer)}>
          {children}
        </div>
        <div className="w-full h-px" />
      </motion.div>
    </div>
  );
});

CollapsableContainer.displayName = "CollapsableContainer";

CollapsableContainer.propTypes = {
  children: node.isRequired,
  title: node.isRequired,
  className: shape({
    headerContainer: string,
    headerTitle: string,
    headerSubTitle: string,
    bodyContainer: string,
  }),
  defaultOpen: bool,
  isDisabled: bool,
  isHeadTextHidden: bool,
  isOpen: bool,
  onChange: func,
  subTitle: node,
  titleIndex: number,
  titleAsHeader: bool,
};

export default CollapsableContainer;
